import { ex } from '@fullcalendar/core/internal-common';
import { CourseCategory, CourseData, CourseLessonTranslations } from './Tables';
import { Dispatch, SetStateAction } from 'react';
import { ISODate } from './Leads';

export interface PlainCourse {
  categories: CourseCategory[];
  courseImage: string;
  courseStateStatus: string;
  createdAt: string;
  description: string;
  id: number;
  name: string;
  translations: CourseLessonTranslations[];
  updatedAt: string;
}

export interface LessonWithRelationsToDelete {
  lessonId: number;
  courseId: number;
  lessonTitle: string;
  videoId: number;
  totalExamsQty: number;
  totalAssistantExamsQty: number;
  futureExamsQty: number;
  futureAssistantExamsQty: number;
}
export interface ICourse {
  id: string;
  name: string;
  description: string | null;
  createdAt: string | null;
  translations: CourseLessonTranslations[];
  userCourseStatus: 'in_progress' | 'completed' | null;
  status: string | null;
  totalDuration: number | null;
  courseImage: string | null;
  categories: CourseCategory[];
  studentCourses: {
    completedAt: ISODate | null;
    courseId: number;
    enrolledAt: ISODate;
    id: number;
    status: 'in_progress' | 'completed' | null;
    studentId: number;
  };
}
export interface LessonVideo {
  filename: string;
  id: number;
  length: number;
  lessonId: number | null;
  link: string;
  screenshots: string[];
  signedExp: number;
  signedSrc: string;
  threadId: string;
  threadId_old: string;
  transcription: string;
  uploadedAt: string;
  videoUrl: string | null;
  transcriptionTokens: number;
  filename_en?: string | null;
  filename_uk?: string | null;
  filename_ru?: string | null;
  playlist_pos?: number | null;
}

export interface PrevNextLesson {
  categoryId: number;
  courseId: number;
  description: string;
  id: number;
  lessonCover: string;
  lesson_pos: number;
  nonScore: boolean;
  title: string;
  videoId: number;
  translations: CourseLessonTranslations[];
}

export interface StudentCourseLesson {
  id: string;
  title: string;
  description: string | null;
  videoId: number;
  course: ICourse;
  video?: LessonVideo[];
  lessonCover: string;
  translations: CourseLessonTranslations[];
  studentScores?: {
    id: number;
    studentId: number;
    lessonId: number;
    courseId: number;
    score: number;
    comment: string | null;
    createdAt: Date;
    updatedAt: Date;
    status: string;
  }[];
  nextLesson?: PrevNextLesson | null;
  prevLesson?: PrevNextLesson | null;
  isCurrentlyProcessingVideo: boolean;
  conversationId: string | null;
}

export interface StudentCourseCategory {
  id: string;
  name: string;
  lessons: StudentCourseLesson[];
}

export interface StudentCourse {
  courseId: string | number;
  name: string;
  categories: StudentCourseCategory[];
  numberOfLessons: number;
}
export interface CourseLessonDetailsData {
  categories: StudentCourseCategory[];
  course: {
    courseImage: string;
    createdAt: string;
    description: string;
    id: number;
    name: string;
  };
  isSuccess?: boolean;
}

// export interface LearningListContainerProps {
//   coursesData?: CourseData[];
//   courseLessonsData?: CourseLessonDetailsData;
//   filters: {
//     category: number[]; // optional for shared typing
//     search: string;
//     duration: number[];
//   };
//   setFilters: Dispatch<
//     SetStateAction<{
//       category: number[];
//       search: string;
//       duration: number[];
//     }>
//   >;
//   categoriesFilterSelectors?: {
//     categories: { filterName: string; filterSelectors: { id: number; name: string }[] };
//   };
//   durationFilterSelectors: {
//     duration: { filterName: string; filterSelectors: { id: number; name: string }[] };
//   };
// }
export enum CourseStateStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PENDING = 'pending',
}
export type StatusFilter = ('in_progress' | 'completed' | 'pending')[]; // Optional alias for reuse
export interface CheckboxListsProps {
  categoriesFilterSelectors: {
    categories: { filterName: string; filterSelectors: { id: number; name: string }[] };
  } | null;
  durationFilterSelectors: {
    duration: { filterName: string; filterSelectors: { id: number; name: string }[] };
  };
  status: StatusFilter;
  setFilters: Dispatch<SetStateAction<any>>; // Can later narrow to union if needed
}

export type BaseFilters = {
  search: string;
  duration: number[];
  status: StatusFilter;
  courseStateStatus?: CourseStateStatus[]; // ✅ Add this line
};

export type LessonFilters = BaseFilters & {
  category: number[];
};

type BaseFilterSelectors = {
  durationFilterSelectors: {
    duration: { filterName: string; filterSelectors: { id: number; name: string }[] };
  };
};

type LessonFilterSelectors = BaseFilterSelectors & {
  categoriesFilterSelectors: {
    categories: { filterName: string; filterSelectors: { id: number; name: string }[] };
  };
};

export type LessonFilterProps = {
  filters: LessonFilters;
  setFilters: Dispatch<SetStateAction<LessonFilters>>;
  status: StatusFilter;
} & LessonFilterSelectors;

export type CourseFilterProps = {
  filters: BaseFilters;
  status: StatusFilter;
  setFilters: Dispatch<SetStateAction<BaseFilters>>;
} & BaseFilterSelectors;

export type CourseProps = {
  coursesData: CourseData[];
  courseLessonsData?: undefined;
  filters: BaseFilters;
  setFilters: Dispatch<SetStateAction<BaseFilters>>;
  durationFilterSelectors: {
    duration: { filterName: string; filterSelectors: { id: number; name: string }[] };
  };
  categoriesFilterSelectors?: undefined;
  status: StatusFilter;
};

export type LessonProps = {
  courseLessonsData: CourseLessonDetailsData;
  coursesData?: undefined;
  filters: LessonFilters;
  setFilters: Dispatch<SetStateAction<LessonFilters>>;
  durationFilterSelectors: {
    duration: { filterName: string; filterSelectors: { id: number; name: string }[] };
  };
  categoriesFilterSelectors: {
    categories: { filterName: string; filterSelectors: { id: number; name: string }[] };
  };
  status: StatusFilter;
};

export type LearningListContainerProps = CourseProps | LessonProps;

interface CourseFilterCardProps {
  filters: BaseFilters;
  setFilters: Dispatch<SetStateAction<BaseFilters>>;
  durationFilterSelectors: BaseFilterSelectors['durationFilterSelectors'];
  status: StatusFilter;
  title?: string; // Optional title for the filter card
}

interface LessonFilterCardProps {
  filters: LessonFilters;
  setFilters: Dispatch<SetStateAction<LessonFilters>>;
  categoriesFilterSelectors: LessonFilterSelectors['categoriesFilterSelectors'];
  durationFilterSelectors: LessonFilterSelectors['durationFilterSelectors'];
  status: StatusFilter;
  title?: string;
}

export type FindCourseCardProps = CourseFilterCardProps | LessonFilterCardProps;
